import {
  ActionType,
  CreatePriceMode as ApiCreatePriceMode,
  type CreatePriceExperienceRowDto,
  type CreatePriceRowDto,
  type CreatePriceSessionQueryParametersDto,
  PriceStatus,
  type SaveCreatePriceRequest,
  type SaveCreatePriceSessionRequest,
} from '@/api';
import {
  formatBoolean,
  formatDate,
  formatDecimal,
  formatFactorField,
  formatForecastedDemand,
  formatIndex,
  formatLaunchStatusDisplay,
  formatPieceCount,
  formatPriceField,
  formatRm,
  formatVat,
  formatYesNo,
  round,
} from '@/components/table/table-format-helpers';
import {
  calculateHasFutureLpChange,
  computeLp,
  computeRm,
  computeRrp,
  getDecimals,
  getDecimalsForSave,
  getDisplayDecimals,
  updateLpDerivedFields,
  updateRrpDerivedFields,
} from '@/components/table/table-helpers';
import type {
  EditableField,
  LockableField,
} from '@/components/table/table-types';
import { formatDateStringIso, parseDateString } from '@/utils/date-utils';
import { LP_MIN_THRESHOLD } from '@/utils/lp-validation';
import Big from 'big.js';
import {
  type CreatePriceModalFilters,
  CreatePriceMode,
} from './create-price-search-params';
import type {
  EditableFieldKey,
  HeaderNames,
  RowData,
  RowDataForDisplay,
} from './types/create-price-table-types';

export const EDITABLE_PRICE_COLUMN_KEYS: readonly string[] = [
  'rrp',
  'rm',
  'lp',
];
export const PRICE_STATUS_COLUMN_KEY = 'priceStatus';

export const shouldHighlightColumn = (
  columnKey: string,
  isApproveMode: boolean,
): boolean => {
  if (isApproveMode) return columnKey === PRICE_STATUS_COLUMN_KEY;
  return EDITABLE_PRICE_COLUMN_KEYS.includes(columnKey);
};

// GL RRP Index = ((RRP / global lead currency rate) / global lead RRP) * 100.
export const computeGlobalLeadRrpIndex = (
  rrp: number,
  globalLeadRrp: number,
  globalLeadCurrencyRate: number,
) => {
  if (globalLeadCurrencyRate === 0 || globalLeadRrp === 0) return 0;
  const result = new Big(rrp)
    .div(globalLeadCurrencyRate)
    .div(globalLeadRrp)
    .times(100);
  return Number(result);
};

export function getHeaderNames(
  rawData: CreatePriceExperienceRowDto[],
  filters: CreatePriceModalFilters,
): HeaderNames {
  const priceUnitIds = new Set(filters.priceUnitIds);
  const productIds = new Set(filters.productIds);
  const themeIds = new Set(filters.themeIds);
  const groupIds = new Set(filters.groupIds);

  const priceUnits = new Map<number, string>();
  const products = new Map<number, string>();
  const themes = new Map<string, string>();
  const groups = new Map<string, string>();

  for (const row of rawData) {
    if (priceUnitIds.has(row.priceUnitId) && row.priceUnitName)
      priceUnits.set(row.priceUnitId, row.priceUnitName);
    if (productIds.has(row.productId) && row.productName)
      products.set(row.productId, row.productName);
    if (themeIds.has(row.themeIdAsString) && row.themeName)
      themes.set(row.themeIdAsString, row.themeName);
    if (groupIds.has(row.groupIdAsString) && row.groupName)
      groups.set(row.groupIdAsString, row.groupName);
  }

  return {
    priceUnits: [...priceUnits.values()],
    products: [...products.values()],
    themes: [...themes.values()],
    groups: [...groups.values()],
  };
}

export const calculatePricingField = (
  field: EditableField,
  row: RowData,
  value: number,
  lockedColumn: LockableField | undefined,
  originalLp: number,
) => {
  if (field === 'vat') return row;

  // Round an entered LP/RRP first; every dependent value (LP, RRP, RM) is calculated from
  // the rounded value. RM/VAT entries are used as typed.
  const decimals = getDecimals(row);
  const committed =
    field === 'lp' || field === 'rrp' ? round(value, decimals) : value;

  if (row[field] === committed) {
    return row;
  }

  const updated = { ...row, [field]: committed };

  const vat = row.vat ?? 0;

  switch (lockedColumn) {
    case 'rrp':
      if (field === 'lp' && row.rrp !== 0) {
        updated.rm = computeRm(row.rrp, committed, vat);
      } else if (field === 'rm') {
        updated.lp = computeLp(row.rrp, committed, vat);
      }
      break;

    case 'rm':
      if (field === 'rrp') {
        updated.lp = computeLp(committed, row.rm, vat);
      } else if (field === 'lp') {
        updated.rrp = computeRrp(committed, row.rm, vat);
      }
      break;

    case 'lp':
      if (field === 'rrp') {
        updated.rm = committed === 0 ? 0 : computeRm(committed, row.lp, vat);
      } else if (field === 'rm') {
        updated.rrp = computeRrp(row.lp, committed, vat);
      }
      break;

    default:
      if (field === 'rrp') {
        if (committed === 0) {
          updated.rm = 0;
        } else {
          updated.lp = computeLp(committed, row.rm, vat);
        }
      } else if (field === 'rm') {
        updated.lp = computeLp(row.rrp, committed, vat);
      } else if (field === 'lp' && row.rrp !== 0) {
        updated.rm = computeRm(row.rrp, committed, vat);
      }
      break;
  }

  // Round the calculated price; RM is kept as calculated
  if (updated.lp !== row.lp) updated.lp = round(updated.lp, decimals);
  if (updated.rrp !== row.rrp) updated.rrp = round(updated.rrp, decimals);

  if (updated.lp !== row.lp) {
    updateLpDerivedFields(updated, row, row.localLeadCurrencyRate);
  }

  if (updated.rrp !== row.rrp) {
    updateRrpDerivedFields(updated, row, row.localLeadCurrencyRate);
    updated.rrpIndexGlobalLead = computeGlobalLeadRrpIndex(
      updated.rrp,
      row.globalLeadRrp,
      row.globalLeadCurrencyRate,
    );
  }

  updated.hasFutureLpChange = calculateHasFutureLpChange(
    updated.lp,
    updated.nearestFutureModifiedLp,
  );

  return updated;
};

export const formatDataForDisplay = (
  dataToFormat: Map<string, RowData>,
): RowDataForDisplay[] => {
  return Array.from(dataToFormat.values()).map((row) => ({
    ...row,
    localLaunchDate: row.localLaunchDate,
    localExitDate: row.localExitDate,
    license: formatYesNo(row.license),
    isRareItem: formatYesNo(row.isRareItem),
    businessToBusinessLaunchDate: formatDate(row.businessToBusinessLaunchDate),
    directToConsumerLifelong: formatYesNo(row.directToConsumerLifelong),
    pieceCount: formatPieceCount(row.pieceCount),
    launchStatus: formatLaunchStatusDisplay(row.launchStatus),
    rrp: formatPriceField(row.rrp, false, getDisplayDecimals(row)),
    localLeadRrp: formatPriceField(row.localLeadRrp),
    globalLeadRrp: formatPriceField(row.globalLeadRrp),
    rrpIndexLocalLead: formatIndex(row.rrpIndexLocalLead),
    rrpIndexGlobalLead: formatIndex(row.rrpIndexGlobalLead),
    vat: formatVat(row.vat),
    rm: formatRm(row.rm),
    localLeadLp: formatPriceField(row.localLeadLp),
    lp: formatPriceField(row.lp, true, getDisplayDecimals(row)),
    lpIndexLocalLead: formatIndex(row.lpIndexLocalLead),
    priceStatus: row.priceStatus?.slice(0, 1) ?? '',
    hasFutureLpChange: formatYesNo(row.hasFutureLpChange),
    lpIndexGlobalLead: formatIndex(row.lpIndexGlobalLead),
    listPriceFmcFactor: formatFactorField(row.listPriceFmcFactor),
    fmcLocal: formatPriceField(row.fmcLocal),
    forecastedDemand: formatForecastedDemand(row.forecastedDemand),
    localLeadCurrencyRate: formatDecimal(row.localLeadCurrencyRate, 4),
    rrpCurrency: row.useRrp && row.currency ? row.currency : '',
    hasPriceChange: formatBoolean(row.hasPriceChange),
  }));
};

export const isPriceStatusChangedFromPreliminary = (
  row: RowDataForDisplay,
  initialData: Map<string, RowData>,
) => {
  const originalRow = initialData.get(row.primaryKey);
  if (!originalRow) return false;
  return (
    originalRow.priceStatus === PriceStatus.Preliminary &&
    row.priceStatus === 'A'
  );
};

export interface ChangeSummary {
  label: string;
  count: number;
}

export interface CategorizedModifiedRows {
  priceChanges: RowData[];
  priceChangesWithApprovals: RowData[];
  statusOnlyApprovals: RowData[];
  all: RowData[];
}

export const getModifiedRowsCategorized = (
  currentData: Map<string, RowData>,
  initialData: Map<string, RowData>,
  isApproveMode = false,
): CategorizedModifiedRows => {
  const priceChanges: RowData[] = [];
  const statusOnlyApprovals: RowData[] = [];

  for (const [key, current] of currentData) {
    const original = initialData.get(key);
    if (!original) continue;

    const hasPriceChange =
      current.rrp !== original.rrp || current.lp !== original.lp;
    const hasStatusChange = current.priceStatus !== original.priceStatus;

    if (hasPriceChange || hasStatusChange) {
      if (isApproveMode) {
        statusOnlyApprovals.push(current);
      } else if (hasPriceChange) {
        priceChanges.push(current);
      } else {
        statusOnlyApprovals.push(current);
      }
    }
  }

  const priceChangesWithApprovals = priceChanges.filter(
    (row) => row.priceStatus !== initialData.get(row.primaryKey)?.priceStatus,
  );

  return {
    priceChanges,
    priceChangesWithApprovals: [
      ...priceChangesWithApprovals,
      ...statusOnlyApprovals,
    ],
    statusOnlyApprovals,
    all: [...priceChanges, ...statusOnlyApprovals],
  };
};

export const formatPriceChangePayload = (
  rows: RowData[],
  validFrom: Date,
  initialData?: Map<string, RowData>,
): SaveCreatePriceRequest => ({
  actionType: ActionType.Change,
  mode: ApiCreatePriceMode.Create,
  validFromDate: validFrom,
  priceRows: rows
    .map((row) => {
      const original = initialData?.get(row.primaryKey);
      // Only check rrp/lp — rm is derived and not sent to backend
      const hasPriceChange =
        row.rrp !== original?.rrp || row.lp !== original?.lp;

      // Skip rows where price didn't actually change
      if (!hasPriceChange) {
        return null;
      }

      return {
        productId: row.productId,
        priceUnitId: row.priceUnitId,
        rrp: round(row.rrp, getDecimalsForSave(row)),
        lp: round(row.lp, getDecimalsForSave(row)),
        // Always send original status for price changes (backend requires status to stay same)
        priceStatus:
          (original?.priceStatus as PriceStatus) ?? PriceStatus.Preliminary,
      };
    })
    .filter((row) => row !== null) as Array<{
    productId: number;
    priceUnitId: number;
    rrp: number;
    lp: number;
    priceStatus: PriceStatus;
  }>,
});

export const formatApprovalPayload = (
  rows: RowData[],
  validFrom: Date,
): SaveCreatePriceRequest => ({
  actionType: ActionType.Approval,
  mode: ApiCreatePriceMode.Approve,
  validFromDate: validFrom,
  priceRows: rows.map((row) => ({
    productId: row.productId,
    priceUnitId: row.priceUnitId,
    rrp: round(row.rrp, getDecimalsForSave(row)),
    lp: round(row.lp, getDecimalsForSave(row)),
    priceStatus: PriceStatus.Approved,
  })),
});

export const getModifiedRowsForSession = (
  current: Map<string, RowData>,
  initial: Map<string, RowData>,
): CreatePriceRowDto[] => {
  const modified: CreatePriceRowDto[] = [];
  for (const [key, row] of current) {
    const original = initial.get(key);
    if (!original) continue;
    if (row.lp < LP_MIN_THRESHOLD && original.lp > 0) continue;
    if (
      row.rrp !== original.rrp ||
      row.lp !== original.lp ||
      row.priceStatus !== original.priceStatus
    ) {
      modified.push({
        productId: row.productId,
        priceUnitId: row.priceUnitId,
        // full precision on purpose: this is a draft, not the committed price (see formatPriceChangePayload),
        // rounding here would make a session reload/resync recompute RM from a rounded lp and drift it
        rrp: row.rrp,
        lp: row.lp,
        priceStatus:
          (row.priceStatus as PriceStatus) ?? PriceStatus.Preliminary,
      });
    }
  }
  return modified;
};

export const buildSessionQueryParameters = (
  filters: CreatePriceModalFilters,
): CreatePriceSessionQueryParametersDto => ({
  validFromDate: filters.validFromDate,
  priceUnitIds: filters.priceUnitIds.length > 0 ? filters.priceUnitIds : null,
  productIds: filters.productIds.length > 0 ? filters.productIds : null,
  themeIds: filters.themeIds.length > 0 ? filters.themeIds : null,
  groupIds: filters.groupIds.length > 0 ? filters.groupIds : null,
  productStatus: {
    firstHalfYear: filters.productStatus.firstHalfYear,
    secondHalfYear: filters.productStatus.secondHalfYear,
    carryOvers: filters.productStatus.carryOvers,
    outgoing: filters.productStatus.outgoing,
  },
  priceStatus: {
    approved: filters.priceStatus.approved,
    preliminary: filters.priceStatus.preliminary,
  },
  onlyZeroPrices: filters.onlyZeroPrices,
});

export const sessionQueryParametersToFilters = (
  params: CreatePriceSessionQueryParametersDto,
): CreatePriceModalFilters => ({
  validFromDate: parseDateString(
    formatDateStringIso(new Date(params.validFromDate)),
  ),
  priceUnitCategoryId: null,
  priceUnitIds: params.priceUnitIds ?? [],
  productIds: params.productIds ?? [],
  themeIds: params.themeIds ?? [],
  groupIds: params.groupIds ?? [],
  productStatus: {
    firstHalfYear: params.productStatus?.firstHalfYear ?? false,
    secondHalfYear: params.productStatus?.secondHalfYear ?? false,
    carryOvers: params.productStatus?.carryOvers ?? false,
    outgoing: params.productStatus?.outgoing ?? false,
  },
  priceStatus: {
    approved: params.priceStatus?.approved ?? false,
    preliminary: params.priceStatus?.preliminary ?? false,
  },
  onlyZeroPrices: params.onlyZeroPrices,
});

export const isApproveCreatePriceMode = (mode: CreatePriceMode): boolean =>
  mode === CreatePriceMode.Approve;

export const buildSessionSaveRequest = (
  mode: CreatePriceMode,
  filters: CreatePriceModalFilters,
  current: Map<string, RowData>,
  initial: Map<string, RowData>,
): SaveCreatePriceSessionRequest => ({
  mode: mode as ApiCreatePriceMode,
  _queryParameters: buildSessionQueryParameters(filters),
  modifiedRows: getModifiedRowsForSession(current, initial),
});

export const mergeSessionWithFreshData = (
  freshData: CreatePriceExperienceRowDto[],
  modifiedRows: CreatePriceRowDto[] | null,
  includeSessionOnlyRows = false,
): Map<string, RowData> => {
  const modifiedMap = new Map(
    (modifiedRows ?? []).map((r) => [`${r.productId}-${r.priceUnitId}`, r]),
  );

  const result = new Map<string, RowData>();
  const matchedKeys = new Set<string>();

  for (const item of freshData) {
    const key = item.primaryKey;
    const lookupKey = `${item.productId}-${item.priceUnitId}`;
    const modified = modifiedMap.get(lookupKey);

    const lp = modified?.lp ?? item.lp;
    const hasFutureLpChange = calculateHasFutureLpChange(
      lp,
      item.nearestFutureModifiedLp,
    );

    if (modified) {
      matchedKeys.add(lookupKey);
      const editedFields = new Set<EditableFieldKey>();
      if (modified.rrp != null) editedFields.add('rrp');
      if (modified.lp != null) editedFields.add('lp');
      if (modified.priceStatus) editedFields.add('priceStatus');

      const rrp = modified.rrp ?? item.rrp;
      const vat = item.vat ?? 0;
      const rm = rrp ? computeRm(rrp, lp, vat) : item.rm;

      const restored: RowData = {
        ...item,
        rrp,
        lp,
        rm,
        priceStatus: modified.priceStatus,
        hasPriceChange: true,
        hasFutureLpChange,
        rrpCurrency: item.currency ?? '',
        editedFields,
      };

      if (lp !== item.lp) {
        updateLpDerivedFields(restored, item, item.localLeadCurrencyRate);
      }
      if (rrp !== item.rrp) {
        updateRrpDerivedFields(restored, item, item.localLeadCurrencyRate);
        restored.rrpIndexGlobalLead = computeGlobalLeadRrpIndex(
          restored.rrp,
          item.globalLeadRrp,
          item.globalLeadCurrencyRate,
        );
      }

      result.set(key, restored);
    } else {
      result.set(key, {
        ...item,
        hasPriceChange: false,
        hasFutureLpChange,
        rrpCurrency: item.currency ?? '',
      });
    }
  }

  if (includeSessionOnlyRows) {
    for (const [lookupKey, modified] of modifiedMap) {
      if (matchedKeys.has(lookupKey)) continue;
      const syntheticKey = `${modified.productId} ${modified.priceUnitId}`;
      result.set(syntheticKey, {
        primaryKey: syntheticKey,
        productId: modified.productId,
        priceUnitId: modified.priceUnitId,
        rrp: modified.rrp ?? 0,
        lp: modified.lp,
        rm: 0,
        priceStatus: modified.priceStatus,
        hasPriceChange: true,
        hasFutureLpChange: calculateHasFutureLpChange(modified.lp, undefined),
        rrpCurrency: '',
        editedFields: new Set<EditableFieldKey>(['priceStatus']),
      } as RowData);
    }
  }

  return result;
};
